import { useQuery } from '@tanstack/react-query';
import { type MessageKey, t } from '../../i18n';
import { es } from '../../i18n/es';
import { api } from '../../lib/api';

// Datos de /api/acceso (roles, permisos y asignaciones, guía 5.2).

export interface Permission {
  key: string;
  module: string;
  description: string;
}

export interface Role {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  requiresMfa: boolean;
  grantsAll: boolean;
  /** ['*'] si el rol tiene todos los permisos (SuperAdmin). */
  permissions: string[];
}

export interface Organization {
  id: string;
  name: string;
  alias: string | null;
  kind: string;
  groupName: string;
}

export interface UserSummary {
  id: string;
  fullName: string;
  email: string;
  organizationId: string;
  status: string;
}

export interface Assignment {
  id: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  organizationId: string;
  expiresAt: string | null;
  expired: boolean;
}

export const KEYS = {
  todo: ['acceso'] as const,
  roles: ['acceso', 'roles'] as const,
  permisos: ['acceso', 'permisos'] as const,
  organizaciones: ['acceso', 'organizaciones'] as const,
  usuarios: (buscar: string) => ['acceso', 'usuarios', buscar] as const,
  asignaciones: (userId: string) => ['acceso', 'asignaciones', userId] as const,
};

export const useRoles = () => useQuery({ queryKey: KEYS.roles, queryFn: () => api<Role[]>('/api/acceso/roles') });
export const usePermissions = () => useQuery({ queryKey: KEYS.permisos, queryFn: () => api<Permission[]>('/api/acceso/permisos'), staleTime: Infinity });
export const useOrganizations = () =>
  useQuery({ queryKey: KEYS.organizaciones, queryFn: () => api<Organization[]>('/api/acceso/organizaciones'), staleTime: 5 * 60_000 });

/** Nombre del módulo de un permiso ("remoto" → "Acceso remoto"); si no hay texto, la clave. */
export function moduleName(module: string): string {
  const key = `modulo.${module}`;
  return key in es ? t(key as MessageKey) : module;
}

/** Agrupa el catálogo por módulo, en el orden en que aparece cada módulo. */
export function byModule(permissions: Permission[]): Array<{ module: string; items: Permission[] }> {
  const groups = new Map<string, Permission[]>();
  for (const p of permissions) groups.set(p.module, [...(groups.get(p.module) ?? []), p]);
  return [...groups].map(([module, items]) => ({ module, items }));
}

export function orgLabel(o: Organization): string {
  return o.alias ? `${o.name} (${o.alias})` : o.name;
}
